import React, { useState, useEffect } from 'react';
import { Clock, AlertCircle, CheckCircle } from 'lucide-react';

interface CountdownTimerProps {
  deadline: string | null;
  eventDate: string;
  eventTime: string;
  theme: 'light' | 'dark';
  className?: string;
}

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
  isExpired: boolean;
  isUrgent: boolean;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({
  deadline,
  eventDate,
  eventTime,
  theme,
  className = ''
}) => {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining | null>(null);

  useEffect(() => {
    if (!deadline) {
      setTimeRemaining(null);
      return;
    }

    const calculateTimeRemaining = (): TimeRemaining => {
      const now = new Date();
      const deadlineDate = new Date(deadline);
      
      // Calculate the event datetime for comparison
      const eventDateTime = new Date(`${eventDate}T${eventTime}`);
      
      // Use the earlier of deadline or event time
      const targetTime = deadlineDate < eventDateTime ? deadlineDate : eventDateTime;
      
      const total = Math.max(0, targetTime.getTime() - now.getTime());
      const isExpired = total <= 0;
      
      if (isExpired) {
        return {
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          total: 0,
          isExpired: true,
          isUrgent: false
        };
      }

      const days = Math.floor(total / (1000 * 60 * 60 * 24));
      const hours = Math.floor((total % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((total % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((total % (1000 * 60)) / 1000);
      
      // Consider urgent if less than 24 hours remaining
      const isUrgent = total < 24 * 60 * 60 * 1000;

      return {
        days,
        hours,
        minutes,
        seconds,
        total,
        isExpired: false,
        isUrgent
      };
    };

    const updateTimer = () => {
      setTimeRemaining(calculateTimeRemaining());
    };

    // Initial calculation
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [deadline, eventDate, eventTime]);

  if (!timeRemaining) {
    return null;
  }

  const getStatusColor = () => {
    if (timeRemaining.isExpired) {
      return theme === 'dark' 
        ? 'text-red-400 border-red-700/50 bg-red-900/30' 
        : 'text-red-600 border-red-200 bg-red-100';
    }
    
    if (timeRemaining.isUrgent) {
      return theme === 'dark' 
        ? 'text-yellow-400 border-yellow-700/50 bg-yellow-900/30' 
        : 'text-yellow-600 border-yellow-200 bg-yellow-100';
    }
    
    return theme === 'dark' 
      ? 'text-[#33FF00] border-[#33FF00]/30 bg-[#33FF00]/10' 
      : 'text-[#33FF00] border-[#33FF00]/20 bg-[#33FF00]/5';
  };

  const getStatusIcon = () => {
    if (timeRemaining.isExpired) {
      return <AlertCircle className="h-4 w-4" />;
    }
    
    if (timeRemaining.isUrgent) {
      return <Clock className="h-4 w-4" />;
    }
    
    return <CheckCircle className="h-4 w-4" />;
  };

  const getStatusText = () => {
    if (timeRemaining.isExpired) {
      return 'Response window has closed';
    }
    
    if (timeRemaining.isUrgent) {
      return 'Response window closing soon';
    }
    
    return 'Time remaining to respond';
  };

  const getDeadlineText = () => {
    if (!deadline) return null;
    
    const deadlineDate = new Date(deadline);
    const eventDateTime = new Date(`${eventDate}T${eventTime}`);
    const targetTime = deadlineDate < eventDateTime ? deadlineDate : eventDateTime;
    
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    
    return `Response deadline: ${targetTime.toLocaleDateString('en-US', options)}`;
  };

  const getTimeDisplay = () => {
    if (timeRemaining.isExpired) {
      return 'Expired';
    }

    const { days, hours, minutes, seconds } = timeRemaining;
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  return (
    <div className={`rounded-lg border p-4 ${getStatusColor()} ${className}`}>
      <div className="flex items-center space-x-2 mb-2">
        {getStatusIcon()}
        <span className="text-sm font-medium">
          {getStatusText()}
        </span>
      </div>
      
      <div className="flex items-center justify-between">
        <div className="text-2xl font-bold font-mono">
          {getTimeDisplay()}
        </div>
        
        {!timeRemaining.isExpired && (
          <div className="text-right">
            <div className="text-xs opacity-75">
              {timeRemaining.days > 0 && `${timeRemaining.days} days, `}
              {timeRemaining.hours > 0 && `${timeRemaining.hours} hours, `}
              {timeRemaining.minutes > 0 && `${timeRemaining.minutes} minutes`}
              {timeRemaining.days === 0 && timeRemaining.hours === 0 && timeRemaining.minutes === 0 && 'seconds'}
            </div>
          </div>
        )}
      </div>
      
      {timeRemaining.isExpired && (
        <div className="mt-2 text-xs opacity-75">
          The response deadline has passed. You can no longer change your response.
        </div>
      )}
      
      {timeRemaining.isUrgent && !timeRemaining.isExpired && (
        <div className="mt-2 text-xs opacity-75">
          Please respond soon to secure your spot at this event.
        </div>
      )}
      
      {getDeadlineText() && (
        <div className="mt-2 text-xs opacity-60 border-t pt-2">
          {getDeadlineText()}
        </div>
      )}
    </div>
  );
};

export default CountdownTimer;
